import React, { useState, useRef, Fragment, createElement } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { MaternityFile } from '../../types';
import { Search, FileText, Printer, Download, Baby, X, Trash2 } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
export const MaternityFiles: React.FC = () => {
  const {
    maternityFiles,
    deleteMaternityFile,
    currentUser
  } = useData();
  const isAdmin = currentUser?.role === 'Administrator';
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFile, setSelectedFile] = useState<MaternityFile | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const handleDeleteFile = (file: MaternityFile) => {
    if (window.confirm(`WARNING: This will permanently delete the maternity file for ${file.patient.firstName} ${file.patient.surname} (${file.bmcFileNumber}) and ALL of its records. This cannot be undone. Are you sure?`)) {
      deleteMaternityFile(file.id);
      setSelectedFile(null);
    }
  };
  const filteredFiles = maternityFiles.filter(m => {
    const q = searchQuery.toLowerCase();
    return m.patient.firstName.toLowerCase().includes(q) || m.patient.surname.toLowerCase().includes(q) || m.bmcFileNumber.toLowerCase().includes(q);
  }).sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  const handlePrint = () => {
    document.body.classList.add('printing-maternity-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-maternity-modal');
    }, 500);
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedFile) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_Maternity_${selectedFile.bmcFileNumber}_${selectedFile.patient.firstName}_${selectedFile.patient.surname}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  return <div className="space-y-6" data-id="element-3043">
      <div className="flex justify-between items-center print:hidden" data-id="element-3044">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-3045">
          Maternity Patient Files
        </h1>
      </div>

      {!selectedFile ? <Card noPadding data-id="element-3046">
          <div className="p-4 border-b border-gray-200 bg-gray-50" data-id="element-3047">
            <div className="relative max-w-md" data-id="element-3048">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3049">
                <Search className="h-5 w-5 text-gray-400" data-id="element-3050" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-pink-500 focus:border-pink-500 sm:text-sm" placeholder="Search by name or BMC file number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-3051" />
            </div>
          </div>
          <ul className="divide-y divide-gray-200" data-id="element-3052">
            {filteredFiles.length > 0 ? filteredFiles.map(file => <li key={file.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-3053">
                  <div className="flex items-center" data-id="element-3054">
                    <div className="bg-pink-100 p-2 rounded-full mr-4" data-id="element-3055">
                      <Baby className="h-6 w-6 text-pink-600" data-id="element-3056" />
                    </div>
                    <div data-id="element-3057">
                      <p className="text-sm font-medium text-blue-600" data-id="element-3058">
                        {file.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-3059">
                        {file.patient.firstName} {file.patient.surname}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-3060">
                        Registered:{' '}
                        {new Date(file.createdAt).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center space-x-4" data-id="element-3061">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${file.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-3062">
                      {file.stage === 'COMPLETED' ? '✓ Completed' : 'Active (Nursing)'}
                    </span>
                    <Button onClick={() => setSelectedFile(file)} data-id="element-3063">
                      <FileText className="w-4 h-4 mr-2" data-id="element-3064" /> View File
                    </Button>
                    {isAdmin && <Button variant="danger" onClick={() => handleDeleteFile(file)} data-id="element-3065">
                        <Trash2 className="w-4 h-4 mr-2" data-id="element-3066" /> Delete
                      </Button>}
                  </div>
                </li>) : <li className="p-12 text-center text-gray-500" data-id="element-3067">
                <Baby className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-3068" />
                <p className="text-lg font-medium text-gray-900" data-id="element-3069">
                  No maternity files found
                </p>
              </li>}
          </ul>
        </Card> : <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-3070">
          <div className="bg-white rounded-lg shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible maternity-modal-print" data-id="element-3071">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-3072">
              <h3 className="text-lg font-semibold text-gray-900" data-id="element-3073">
                Maternity File — {selectedFile.bmcFileNumber}
              </h3>
              <div className="flex items-center space-x-2" data-id="element-3074">
                <Button variant="outline" size="sm" onClick={handlePrint} data-id="element-3075">
                  <Printer className="w-4 h-4 mr-1" data-id="element-3076" /> Print
                </Button>
                <Button variant="primary" size="sm" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-3077">
                  <Download className="w-4 h-4 mr-1" data-id="element-3078" />{' '}
                  {isGeneratingPdf ? 'Generating...' : 'Download PDF'}
                </Button>
                {isAdmin && <Button variant="danger" size="sm" onClick={() => handleDeleteFile(selectedFile)} data-id="element-3079">
                    <Trash2 className="w-4 h-4 mr-1" data-id="element-3080" /> Delete
                  </Button>}
                <button onClick={() => setSelectedFile(null)} className="p-1.5 hover:bg-gray-100 rounded" data-id="element-3081">
                  <X className="w-5 h-5 text-gray-500" data-id="element-3082" />
                </button>
              </div>
            </div>

            <div ref={printableRef} className="p-8 bg-white text-sm font-sans text-black" data-id="element-3083">
              {/* Header */}
              <div className="flex justify-between items-start mb-8 border-b-2 border-black pb-4" data-id="element-3084">
                <div className="flex items-center" data-id="element-3085">
                  <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-3086" />
                  <div data-id="element-3087">
                    <h1 className="text-xl font-bold uppercase" data-id="element-3088">
                      BMED MEDICAL CENTRE
                    </h1>
                    <p className="text-xs italic" data-id="element-3089">Caring Healing Leading</p>
                  </div>
                </div>
                <div className="text-right" data-id="element-3090">
                  <h2 className="text-lg font-bold uppercase tracking-widest border-b border-black pb-1 mb-2" data-id="element-3091">
                    Maternity Record
                  </h2>
                  <p data-id="element-3092">
                    <span className="font-semibold" data-id="element-3093">File No:</span>{' '}
                    {selectedFile.bmcFileNumber}
                  </p>
                  <p data-id="element-3094">
                    <span className="font-semibold" data-id="element-3095">Registered:</span>{' '}
                    {new Date(selectedFile.createdAt).toLocaleDateString()}
                  </p>
                </div>
              </div>

              {/* PATIENT'S DETAILS */}
              <div className="mb-6" data-id="element-3096">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3097">
                  PATIENT'S DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-3098">
                  <div data-id="element-3099">
                    <span className="font-semibold" data-id="element-3100">First Name:</span>{' '}
                    {selectedFile.patient.firstName}
                  </div>
                  <div data-id="element-3101">
                    <span className="font-semibold" data-id="element-3102">Surname:</span>{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-3103">
                    <span className="font-semibold" data-id="element-3104">Other Names:</span>{' '}
                    {selectedFile.patient.otherNames}
                  </div>
                  <div data-id="element-3105">
                    <span className="font-semibold" data-id="element-3106">Age:</span>{' '}
                    {selectedFile.patient.age}
                  </div>
                  <div data-id="element-3107">
                    <span className="font-semibold" data-id="element-3108">Religion:</span>{' '}
                    {selectedFile.patient.religion}
                  </div>
                  <div data-id="element-3109">
                    <span className="font-semibold" data-id="element-3110">Church Attended:</span>{' '}
                    {selectedFile.patient.churchAttended}
                  </div>
                  <div className="col-span-2" data-id="element-3111">
                    <span className="font-semibold" data-id="element-3112">Residential Address:</span>{' '}
                    {selectedFile.patient.residentialAddress}
                  </div>
                  <div className="col-span-2" data-id="element-3113">
                    <span className="font-semibold" data-id="element-3114">Location/Landmark:</span>{' '}
                    {selectedFile.patient.locationLandmark}
                  </div>
                </div>
              </div>

              {/* SPOUSE DETAILS */}
              <div className="mb-6" data-id="element-3115">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3116">
                  SPOUSE DETAILS (OPTIONAL)
                </h3>
                <div className="grid grid-cols-2 gap-y-2 gap-x-8" data-id="element-3117">
                  <div data-id="element-3118">
                    <span className="font-semibold" data-id="element-3119">First Name:</span>{' '}
                    {selectedFile.spouse.firstName}
                  </div>
                  <div data-id="element-3120">
                    <span className="font-semibold" data-id="element-3121">Surname:</span>{' '}
                    {selectedFile.spouse.surname}
                  </div>
                  <div data-id="element-3122">
                    <span className="font-semibold" data-id="element-3123">Other Names:</span>{' '}
                    {selectedFile.spouse.otherNames}
                  </div>
                  <div data-id="element-3124">
                    <span className="font-semibold" data-id="element-3125">Occupation:</span>{' '}
                    {selectedFile.spouse.occupation}
                  </div>
                  <div className="col-span-2" data-id="element-3126">
                    <span className="font-semibold" data-id="element-3127">Contact:</span>{' '}
                    {selectedFile.spouse.contact}
                  </div>
                </div>
              </div>

              {/* ADMISSION */}
              <div className="mb-6" data-id="element-3128">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3129">
                  ADMISSION
                </h3>
                <div className="flex space-x-16" data-id="element-3130">
                  <div data-id="element-3131">
                    <span className="font-semibold" data-id="element-3132">Date:</span>{' '}
                    {selectedFile.admission.date}
                  </div>
                  <div data-id="element-3133">
                    <span className="font-semibold" data-id="element-3134">Time of Admission:</span>{' '}
                    {selectedFile.admission.time}
                  </div>
                </div>
              </div>

              {/* GENERAL EXAMINATION */}
              <div className="mb-6" data-id="element-3135">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3136">
                  GENERAL EXAMINATION
                </h3>
                <div className="space-y-2" data-id="element-3137">
                  <div data-id="element-3138">
                    <span className="font-semibold" data-id="element-3139">
                      Cardiovascular System:
                    </span>{' '}
                    {selectedFile.generalExam.cardiovascularSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-3140">
                    <div data-id="element-3141">
                      <span className="font-semibold" data-id="element-3142">Temp:</span>{' '}
                      {selectedFile.generalExam.temp}
                    </div>
                    <div data-id="element-3143">
                      <span className="font-semibold" data-id="element-3144">Pulse:</span>{' '}
                      {selectedFile.generalExam.pulse}
                    </div>
                    <div data-id="element-3145">
                      <span className="font-semibold" data-id="element-3146">Respiration:</span>{' '}
                      {selectedFile.generalExam.respiration}
                    </div>
                    <div data-id="element-3147">
                      <span className="font-semibold" data-id="element-3148">BP:</span>{' '}
                      {selectedFile.generalExam.bp}
                    </div>
                  </div>
                  <div className="flex space-x-16" data-id="element-3149">
                    <div data-id="element-3150">
                      <span className="font-semibold" data-id="element-3151">Oedema:</span>{' '}
                      {selectedFile.generalExam.oedema}
                    </div>
                    <div data-id="element-3152">
                      <span className="font-semibold" data-id="element-3153">Anemia:</span>{' '}
                      {selectedFile.generalExam.anemia}
                    </div>
                  </div>
                  <div data-id="element-3154">
                    <span className="font-semibold" data-id="element-3155">Respiratory System:</span>{' '}
                    {selectedFile.generalExam.respiratorySystem}
                  </div>
                  <div data-id="element-3156">
                    <span className="font-semibold" data-id="element-3157">
                      Central Nervous System:
                    </span>{' '}
                    {selectedFile.generalExam.centralNervousSystem}
                  </div>
                  <div className="flex space-x-8" data-id="element-3158">
                    <div data-id="element-3159">
                      <span className="font-semibold" data-id="element-3160">Breasts:</span>{' '}
                      {selectedFile.generalExam.breasts}
                    </div>
                    <div data-id="element-3161">
                      <span className="font-semibold" data-id="element-3162">Varicosities:</span>{' '}
                      {selectedFile.generalExam.varicosities}
                    </div>
                    <div data-id="element-3163">
                      <span className="font-semibold" data-id="element-3164">Vaginal Discharge:</span>{' '}
                      {selectedFile.generalExam.vaginalDischarge}
                    </div>
                  </div>
                  <div data-id="element-3165">
                    <span className="font-semibold" data-id="element-3166">Other Abnormalities:</span>{' '}
                    {selectedFile.generalExam.otherAbnormalities}
                  </div>
                  <div className="flex space-x-8 mt-2" data-id="element-3167">
                    <div data-id="element-3168">
                      <span className="font-semibold" data-id="element-3169">MS Urine: SG:</span>{' '}
                      {selectedFile.generalExam.msUrine.sg}
                    </div>
                    <div data-id="element-3170">
                      <span className="font-semibold" data-id="element-3171">Albumen:</span>{' '}
                      {selectedFile.generalExam.msUrine.albumen}
                    </div>
                    <div data-id="element-3172">
                      <span className="font-semibold" data-id="element-3173">Acetone:</span>{' '}
                      {selectedFile.generalExam.msUrine.acetone}
                    </div>
                    <div data-id="element-3174">
                      <span className="font-semibold" data-id="element-3175">Sugar:</span>{' '}
                      {selectedFile.generalExam.msUrine.sugar}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3176">
                    <div data-id="element-3177">
                      <span className="font-semibold" data-id="element-3178">Hb%:</span>{' '}
                      {selectedFile.generalExam.hb}{' '}
                      <span className="italic text-xs" data-id="element-3179">
                        (NOTE: Refer to UTH if HB% is 6grams or below)
                      </span>
                    </div>
                    <div data-id="element-3180">
                      <span className="font-semibold" data-id="element-3181">ABO:</span>{' '}
                      {selectedFile.generalExam.abo}
                    </div>
                    <div data-id="element-3182">
                      <span className="font-semibold" data-id="element-3183">Rh:</span>{' '}
                      {selectedFile.generalExam.rh}
                    </div>
                  </div>
                </div>
              </div>

              {/* PRESENT PREGNANCY */}
              <div className="mb-6" data-id="element-3184">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3185">
                  PRESENT PREGNANCY
                </h3>
                <div className="space-y-2" data-id="element-3186">
                  <div className="flex space-x-8" data-id="element-3187">
                    <div data-id="element-3188">
                      <span className="font-semibold" data-id="element-3189">LMP:</span>{' '}
                      {selectedFile.presentPregnancy.lmp}
                    </div>
                    <div data-id="element-3190">
                      <span className="font-semibold" data-id="element-3191">Quickening:</span>{' '}
                      {selectedFile.presentPregnancy.quickening}
                    </div>
                    <div data-id="element-3192">
                      <span className="font-semibold" data-id="element-3193">EDD:</span>{' '}
                      {selectedFile.presentPregnancy.edd}
                    </div>
                    <div data-id="element-3194">
                      <span className="font-semibold" data-id="element-3195">Gestation Age:</span>{' '}
                      {selectedFile.presentPregnancy.gestationAge}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3196">
                    <div data-id="element-3197">
                      <span className="font-semibold" data-id="element-3198">
                        Summary of ANC Findings 1st BP:
                      </span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryFirstBp}
                    </div>
                    <div data-id="element-3199">
                      <span className="font-semibold" data-id="element-3200">at:</span>{' '}
                      {selectedFile.presentPregnancy.ancSummaryAtWeeks} Weeks
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3201">
                    <div data-id="element-3202">
                      <span className="font-semibold" data-id="element-3203">
                        Most recent Syphilis Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.date}
                    </div>
                    <div data-id="element-3204">
                      <span className="font-semibold" data-id="element-3205">Result:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.result}
                    </div>
                    <div data-id="element-3206">
                      <span className="font-semibold" data-id="element-3207">Treatment:</span>{' '}
                      {selectedFile.presentPregnancy.syphilisTest.treatment}
                    </div>
                  </div>
                  <div className="flex space-x-8" data-id="element-3208">
                    <div data-id="element-3209">
                      <span className="font-semibold" data-id="element-3210">
                        Most recent HP Test Date:
                      </span>{' '}
                      {selectedFile.presentPregnancy.hpTest.date}
                    </div>
                    <div data-id="element-3211">
                      <span className="font-semibold" data-id="element-3212">Result:</span>{' '}
                      {selectedFile.presentPregnancy.hpTest.result}
                    </div>
                  </div>
                  <div data-id="element-3213">
                    <span className="font-semibold" data-id="element-3214">Pelvic Assessment:</span>{' '}
                    {selectedFile.presentPregnancy.pelvicAssessment}
                  </div>
                  <div data-id="element-3215">
                    <span className="font-semibold" data-id="element-3216">Abnormalities:</span>{' '}
                    {selectedFile.presentPregnancy.abnormalities}
                  </div>
                </div>
              </div>

              {/* OBSERVATIONS FOR LATENT PHASE LABOUR */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3217">
                <h3 className="font-bold uppercase underline mb-1" data-id="element-3218">
                  OBSERVATIONS FOR LATENT PHASE LABOUR
                </h3>
                <p className="text-xs italic mb-2" data-id="element-3219">
                  NOTE: Refer to UTH if latest phase exceeds 8 hours
                </p>
                <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-3220">
                  <thead data-id="element-3221">
                    <tr className="bg-gray-100" data-id="element-3222">
                      <th className="border border-black p-1" data-id="element-3223">Date</th>
                      <th className="border border-black p-1" data-id="element-3224">Time</th>
                      <th className="border border-black p-1" data-id="element-3225">Pulse</th>
                      <th className="border border-black p-1" data-id="element-3226">BP</th>
                      <th className="border border-black p-1" data-id="element-3227">FH</th>
                      <th className="border border-black p-1" data-id="element-3228">Temp</th>
                      <th className="border border-black p-1" data-id="element-3229">Urinalysis</th>
                      <th className="border border-black p-1" data-id="element-3230">Contractions</th>
                      <th className="border border-black p-1" data-id="element-3231">
                        Cervical Dilation
                      </th>
                      <th className="border border-black p-1" data-id="element-3232">
                        Membranes Liquor
                      </th>
                      <th className="border border-black p-1" data-id="element-3233">Remarks</th>
                      <th className="border border-black p-1" data-id="element-3234">Sign</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3235">
                    {selectedFile.labourObservations.length > 0 ? selectedFile.labourObservations.map(obs => <tr key={obs.id} data-id="element-3236">
                          <td className="border border-black p-1" data-id="element-3237">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3238">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-3239">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-3240">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-3241">{obs.fh}</td>
                          <td className="border border-black p-1" data-id="element-3242">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3243">
                            {obs.urinalysis}
                          </td>
                          <td className="border border-black p-1" data-id="element-3244">
                            {obs.contractions}
                          </td>
                          <td className="border border-black p-1" data-id="element-3245">
                            {obs.cervicalDilation}
                          </td>
                          <td className="border border-black p-1" data-id="element-3246">
                            {obs.membranesLiquor}
                          </td>
                          <td className="border border-black p-1 text-left" data-id="element-3247">
                            {obs.remarks}
                          </td>
                          <td className="border border-black p-1" data-id="element-3248">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-3249">
                        <td colSpan={12} className="border border-black p-4 text-gray-400 italic" data-id="element-3250">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DESCRIPTION OF LABOUR AND DELIVERY */}
              <div className="mb-6" data-id="element-3251">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3252">
                  DESCRIPTION OF LABOUR AND DELIVERY
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3253">
                  <div data-id="element-3254">
                    <span className="font-semibold" data-id="element-3255">Laceration:</span>{' '}
                    {selectedFile.delivery.laceration} |{' '}
                    <span className="font-semibold" data-id="element-3256">Anterior:</span>{' '}
                    {selectedFile.delivery.anterior} |{' '}
                    <span className="font-semibold" data-id="element-3257">Posterior:</span>{' '}
                    {selectedFile.delivery.posterior} |{' '}
                    <span className="font-semibold" data-id="element-3258">None:</span>{' '}
                    {selectedFile.delivery.none}
                  </div>
                  <div data-id="element-3259">
                    <span className="font-semibold" data-id="element-3260">Repaired:</span>{' '}
                    {selectedFile.delivery.repaired}
                  </div>
                  <div data-id="element-3261">
                    <span className="font-semibold" data-id="element-3262">Membranes:</span>{' '}
                    {selectedFile.delivery.membranes}
                  </div>
                  <div data-id="element-3263">
                    <span className="font-semibold" data-id="element-3264">Date:</span>{' '}
                    {selectedFile.delivery.membranesDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3265">Time:</span>{' '}
                    {selectedFile.delivery.membranesTime}
                  </div>
                  <div data-id="element-3266">
                    <span className="font-semibold" data-id="element-3267">Episiotomy:</span>{' '}
                    {selectedFile.delivery.episiotomy}
                  </div>
                  <div data-id="element-3268">
                    <span className="font-semibold" data-id="element-3269">Repaired:</span>{' '}
                    {selectedFile.delivery.episiotomyRepaired}
                  </div>
                  <div data-id="element-3270">
                    <span className="font-semibold" data-id="element-3271">Maternal ARV:</span>{' '}
                    {selectedFile.delivery.maternalARV}
                  </div>
                  <div data-id="element-3272">
                    <span className="font-semibold" data-id="element-3273">
                      Which ones (NPV, AZT, OTHER):
                    </span>{' '}
                    {selectedFile.delivery.arvTypes}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-3274">
                    <div data-id="element-3275">
                      <span className="font-semibold" data-id="element-3276">Uterine massage:</span>{' '}
                      {selectedFile.delivery.uterineMassage}
                    </div>
                    <div data-id="element-3277">
                      <span className="font-semibold" data-id="element-3278">
                        Mother Already on HAART:
                      </span>{' '}
                      {selectedFile.delivery.motherOnHAART}
                    </div>
                    <div data-id="element-3279">
                      <span className="font-semibold" data-id="element-3280">Cytotec:</span>{' '}
                      {selectedFile.delivery.cytotec}
                    </div>
                    <div data-id="element-3281">
                      <span className="font-semibold" data-id="element-3282">Syntometrine:</span>{' '}
                      {selectedFile.delivery.syntometrine}
                    </div>
                  </div>
                  <div data-id="element-3283">
                    <span className="font-semibold" data-id="element-3284">1st stage began Date:</span>{' '}
                    {selectedFile.delivery.firstStageBeganDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3285">Time:</span>{' '}
                    {selectedFile.delivery.firstStageBeganTime}
                  </div>
                  <div data-id="element-3286">
                    <span className="font-semibold" data-id="element-3287">Placenta Delivery:</span>{' '}
                    {selectedFile.delivery.placentaDelivery}
                  </div>
                  <div data-id="element-3288">
                    <span className="font-semibold" data-id="element-3289">
                      Complete dilation Date:
                    </span>{' '}
                    {selectedFile.delivery.completeDilationDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3290">Time:</span>{' '}
                    {selectedFile.delivery.completeDilationTime}
                  </div>
                  <div data-id="element-3291">
                    <span className="font-semibold" data-id="element-3292">
                      Placenta Delivered Date:
                    </span>{' '}
                    {selectedFile.delivery.placentaDeliveredDate}{' '}
                    <span className="font-semibold ml-4" data-id="element-3293">Time:</span>{' '}
                    {selectedFile.delivery.placentaDeliveredTime}
                  </div>
                  <div className="col-span-2" data-id="element-3294">
                    <span className="font-semibold" data-id="element-3295">
                      Abnormality at Dilation:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDilation} |{' '}
                    <span className="font-semibold" data-id="element-3296">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDilationDesc}
                  </div>
                  <div data-id="element-3297">
                    <span className="font-semibold" data-id="element-3298">
                      Postpartum hemorrhage:
                    </span>{' '}
                    {selectedFile.delivery.postpartumHemorrhage}
                  </div>
                  <div data-id="element-3299">
                    <span className="font-semibold" data-id="element-3300">Delivery Method:</span>{' '}
                    {selectedFile.delivery.deliveryMethod}
                  </div>
                  <div data-id="element-3301">
                    <span className="font-semibold" data-id="element-3302">EBL:</span>{' '}
                    {selectedFile.delivery.ebl} cc
                  </div>
                  <div className="col-span-2" data-id="element-3303">
                    <span className="font-semibold" data-id="element-3304">
                      Abnormality at Delivery:
                    </span>{' '}
                    {selectedFile.delivery.abnormalityAtDelivery} |{' '}
                    <span className="font-semibold" data-id="element-3305">Describe:</span>{' '}
                    {selectedFile.delivery.abnormalityAtDeliveryDesc}
                  </div>
                  <div className="col-span-2" data-id="element-3306">
                    <span className="font-semibold" data-id="element-3307">Complications:</span>{' '}
                    {selectedFile.delivery.complications}
                  </div>
                </div>

                <div className="mt-4" data-id="element-3308">
                  <h4 className="font-bold text-sm mb-1" data-id="element-3309">DELIVERY SUMMARY:</h4>
                  <table className="w-full text-xs border-collapse border border-black text-center" data-id="element-3310">
                    <thead data-id="element-3311">
                      <tr className="bg-gray-100" data-id="element-3312">
                        <th className="border border-black p-1" data-id="element-3313">DATE</th>
                        <th className="border border-black p-1" data-id="element-3314">
                          TIME OF BIRTH
                        </th>
                        <th className="border border-black p-1" data-id="element-3315">SEX</th>
                        <th className="border border-black p-1" data-id="element-3316">WEIGHT</th>
                        <th className="border border-black p-1" data-id="element-3317">
                          DELIVERED BY
                        </th>
                      </tr>
                    </thead>
                    <tbody data-id="element-3318">
                      {selectedFile.delivery.deliverySummary.length > 0 ? selectedFile.delivery.deliverySummary.map(sum => <tr key={sum.id} data-id="element-3319">
                            <td className="border border-black p-1" data-id="element-3320">
                              {sum.date}
                            </td>
                            <td className="border border-black p-1" data-id="element-3321">
                              {sum.timeOfBirth}
                            </td>
                            <td className="border border-black p-1" data-id="element-3322">
                              {sum.sex}
                            </td>
                            <td className="border border-black p-1" data-id="element-3323">
                              {sum.weight}
                            </td>
                            <td className="border border-black p-1" data-id="element-3324">
                              {sum.deliveredBy}
                            </td>
                          </tr>) : <tr data-id="element-3325">
                          <td colSpan={5} className="border border-black p-4 text-gray-400 italic" data-id="element-3326">
                            No delivery summary recorded
                          </td>
                        </tr>}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* NEW BORN EVALUATION */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3327">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3328">
                  NEW BORN EVALUATION
                </h3>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3329">
                  <div data-id="element-3330">
                    <span className="font-semibold" data-id="element-3331">EGA:</span>{' '}
                    {selectedFile.newbornEvaluation.ega}
                  </div>
                  <div data-id="element-3332">
                    <span className="font-semibold" data-id="element-3333">ALIVE:</span>{' '}
                    {selectedFile.newbornEvaluation.alive}
                  </div>
                  <div data-id="element-3334">
                    <span className="font-semibold" data-id="element-3335">Neonatal Death:</span>{' '}
                    {selectedFile.newbornEvaluation.neonatalDeath}
                  </div>
                  <div data-id="element-3336">
                    <span className="font-semibold" data-id="element-3337">
                      If still born or death? Cause:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.causeOfDeath}
                  </div>
                  <div className="col-span-2 flex space-x-8" data-id="element-3338">
                    <div data-id="element-3339">
                      <span className="font-semibold" data-id="element-3340">Apgar 1 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar1}
                    </div>
                    <div data-id="element-3341">
                      <span className="font-semibold" data-id="element-3342">Apgar 5 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar5}
                    </div>
                    <div data-id="element-3343">
                      <span className="font-semibold" data-id="element-3344">Apgar 10 Minutes:</span>{' '}
                      {selectedFile.newbornEvaluation.apgar10}
                    </div>
                  </div>
                  <div data-id="element-3345">
                    <span className="font-semibold" data-id="element-3346">General Exam:</span>{' '}
                    {selectedFile.newbornEvaluation.generalExam}
                  </div>
                  <div data-id="element-3347">
                    <span className="font-semibold" data-id="element-3348">
                      Congenital Malformation:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformation}
                  </div>
                  <div className="col-span-2" data-id="element-3349">
                    <span className="font-semibold" data-id="element-3350">If Yes Describe:</span>{' '}
                    {selectedFile.newbornEvaluation.congenitalMalformationDesc}
                  </div>
                  <div data-id="element-3351">
                    <span className="font-semibold" data-id="element-3352">Infant ARVS?:</span>{' '}
                    {selectedFile.newbornEvaluation.infantARVs}
                  </div>
                  <div data-id="element-3353">
                    <span className="font-semibold" data-id="element-3354">Inf Feeding:</span>{' '}
                    {selectedFile.newbornEvaluation.infantFeeding}
                  </div>
                  <div className="col-span-2" data-id="element-3355">
                    <span className="font-semibold" data-id="element-3356">
                      RESUSCITATION METHODS:
                    </span>{' '}
                    {selectedFile.newbornEvaluation.resuscitationMethods}
                  </div>
                </div>

                <div className="mt-4" data-id="element-3357">
                  <h4 className="font-bold text-sm mb-1 underline" data-id="element-3358">
                    NEWBORN EXAMINATION
                  </h4>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3359">
                    <div data-id="element-3360">
                      <span className="font-semibold" data-id="element-3361">CROWN HEEL LENGTH:</span>{' '}
                      {selectedFile.newbornEvaluation.crownHeelLength}
                    </div>
                    <div data-id="element-3362">
                      <span className="font-semibold" data-id="element-3363">HEAD CIRCUMFERENCE:</span>{' '}
                      {selectedFile.newbornEvaluation.headCircumference}
                    </div>
                    <div data-id="element-3364">
                      <span className="font-semibold" data-id="element-3365">Skin: Jaundice:</span>{' '}
                      {selectedFile.newbornEvaluation.skinJaundice}
                    </div>
                    <div data-id="element-3366">
                      <span className="font-semibold" data-id="element-3367">Other:</span>{' '}
                      {selectedFile.newbornEvaluation.skinOther}
                    </div>
                    <div data-id="element-3368">
                      <span className="font-semibold" data-id="element-3369">Fontanelles:</span>{' '}
                      {selectedFile.newbornEvaluation.fontanelles}
                    </div>
                    <div data-id="element-3370">
                      <span className="font-semibold" data-id="element-3371">Eyes:</span>{' '}
                      {selectedFile.newbornEvaluation.eyes} |{' '}
                      <span className="font-semibold" data-id="element-3372">Treatment:</span>{' '}
                      {selectedFile.newbornEvaluation.eyesTreatment}
                    </div>
                    <div data-id="element-3373">
                      <span className="font-semibold" data-id="element-3374">Mouth:</span>{' '}
                      {selectedFile.newbornEvaluation.mouth}
                    </div>
                    <div data-id="element-3375">
                      <span className="font-semibold" data-id="element-3376">Neck:</span>{' '}
                      {selectedFile.newbornEvaluation.neck}
                    </div>
                    <div data-id="element-3377">
                      <span className="font-semibold" data-id="element-3378">Chest:</span>{' '}
                      {selectedFile.newbornEvaluation.chest}
                    </div>
                    <div data-id="element-3379">
                      <span className="font-semibold" data-id="element-3380">Abdomen:</span>{' '}
                      {selectedFile.newbornEvaluation.abdomen}
                    </div>
                    <div data-id="element-3381">
                      <span className="font-semibold" data-id="element-3382">Umbilicus:</span>{' '}
                      {selectedFile.newbornEvaluation.umbilicus}
                    </div>
                    <div data-id="element-3383">
                      <span className="font-semibold" data-id="element-3384">Genitalia:</span>{' '}
                      {selectedFile.newbornEvaluation.genitalia} |{' '}
                      <span className="font-semibold" data-id="element-3385">Urine:</span>{' '}
                      {selectedFile.newbornEvaluation.urine}
                    </div>
                    <div data-id="element-3386">
                      <span className="font-semibold" data-id="element-3387">Anus:</span>{' '}
                      {selectedFile.newbornEvaluation.anus}
                    </div>
                    <div data-id="element-3388">
                      <span className="font-semibold" data-id="element-3389">Meconium:</span>{' '}
                      {selectedFile.newbornEvaluation.meconium}
                    </div>
                    <div data-id="element-3390">
                      <span className="font-semibold" data-id="element-3391">Upper Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.upperLimbs}
                    </div>
                    <div data-id="element-3392">
                      <span className="font-semibold" data-id="element-3393">ERB Paralysis:</span>{' '}
                      {selectedFile.newbornEvaluation.erbParalysis}
                    </div>
                    <div data-id="element-3394">
                      <span className="font-semibold" data-id="element-3395">Lower Limbs:</span>{' '}
                      {selectedFile.newbornEvaluation.lowerLimbs}
                    </div>
                    <div data-id="element-3396">
                      <span className="font-semibold" data-id="element-3397">Ortolani Sign:</span>{' '}
                      {selectedFile.newbornEvaluation.ortolaniSign}
                    </div>
                    <div className="col-span-2" data-id="element-3398">
                      <span className="font-semibold" data-id="element-3399">Back:</span>{' '}
                      {selectedFile.newbornEvaluation.back}
                    </div>
                    <div className="col-span-2 flex space-x-8" data-id="element-3400">
                      <span className="font-semibold" data-id="element-3401">Reflexes:</span>
                      <div data-id="element-3402">
                        <span className="font-semibold" data-id="element-3403">Grasp:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesGrasp}
                      </div>
                      <div data-id="element-3404">
                        <span className="font-semibold" data-id="element-3405">Moro (Startle):</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesMoro}
                      </div>
                      <div data-id="element-3406">
                        <span className="font-semibold" data-id="element-3407">Sucking:</span>{' '}
                        {selectedFile.newbornEvaluation.reflexesSucking}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="mt-4" data-id="element-3408">
                  <span className="font-semibold" data-id="element-3409">NEWBORN PROGRESS NOTES:</span>
                  <p className="mt-1 whitespace-pre-wrap" data-id="element-3410">
                    {selectedFile.newbornEvaluation.progressNotes}
                  </p>
                </div>
              </div>

              {/* POST DELIVERY OBSERVATION CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3411">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3412">
                  POST DELIVERY OBSERVATION CHART
                </h3>
                <table className="w-full text-[10px] border-collapse border border-black text-center" data-id="element-3413">
                  <thead data-id="element-3414">
                    <tr className="bg-gray-100" data-id="element-3415">
                      <th className="border border-black p-1" colSpan={2} data-id="element-3416">
                        TIME
                      </th>
                      <th className="border border-black p-1" colSpan={8} data-id="element-3417">
                        MOTHER
                      </th>
                      <th className="border border-black p-1" colSpan={7} data-id="element-3418">
                        BABY
                      </th>
                      <th className="border border-black p-1" rowSpan={2} data-id="element-3419">
                        SIGN
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3420">
                      <th className="border border-black p-1" data-id="element-3421">DATE</th>
                      <th className="border border-black p-1" data-id="element-3422">TIME</th>
                      <th className="border border-black p-1" data-id="element-3423">BP</th>
                      <th className="border border-black p-1" data-id="element-3424">PULSE</th>
                      <th className="border border-black p-1" data-id="element-3425">RESPS</th>
                      <th className="border border-black p-1" data-id="element-3426">TEMP</th>
                      <th className="border border-black p-1" data-id="element-3427">PV BLEEDING</th>
                      <th className="border border-black p-1" data-id="element-3428">UTERUS</th>
                      <th className="border border-black p-1" data-id="element-3429">BLADDER</th>
                      <th className="border border-black p-1" data-id="element-3430">PALLOR</th>
                      <th className="border border-black p-1" data-id="element-3431">TEMP</th>
                      <th className="border border-black p-1" data-id="element-3432">RESPS</th>
                      <th className="border border-black p-1" data-id="element-3433">CORD</th>
                      <th className="border border-black p-1" data-id="element-3434">SKIN COLOUR</th>
                      <th className="border border-black p-1" data-id="element-3435">
                        FEEDING (BF/AF)
                      </th>
                      <th className="border border-black p-1" data-id="element-3436">BOWEL</th>
                      <th className="border border-black p-1" data-id="element-3437">URINE</th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3438">
                    {selectedFile.postDeliveryObservations.length > 0 ? selectedFile.postDeliveryObservations.map(obs => <tr key={obs.id} data-id="element-3439">
                          <td className="border border-black p-1" data-id="element-3440">
                            {obs.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3441">
                            {obs.time}
                          </td>
                          <td className="border border-black p-1" data-id="element-3442">{obs.bp}</td>
                          <td className="border border-black p-1" data-id="element-3443">
                            {obs.pulse}
                          </td>
                          <td className="border border-black p-1" data-id="element-3444">
                            {obs.resps}
                          </td>
                          <td className="border border-black p-1" data-id="element-3445">
                            {obs.temp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3446">
                            {obs.pvBleeding}
                          </td>
                          <td className="border border-black p-1" data-id="element-3447">
                            {obs.uterus}
                          </td>
                          <td className="border border-black p-1" data-id="element-3448">
                            {obs.bladder}
                          </td>
                          <td className="border border-black p-1" data-id="element-3449">
                            {obs.pallor}
                          </td>
                          <td className="border border-black p-1" data-id="element-3450">
                            {obs.babyTemp}
                          </td>
                          <td className="border border-black p-1" data-id="element-3451">
                            {obs.babyResps}
                          </td>
                          <td className="border border-black p-1" data-id="element-3452">
                            {obs.cord}
                          </td>
                          <td className="border border-black p-1" data-id="element-3453">
                            {obs.skinColour}
                          </td>
                          <td className="border border-black p-1" data-id="element-3454">
                            {obs.feedingBF} / {obs.feedingAF}
                          </td>
                          <td className="border border-black p-1" data-id="element-3455">
                            {obs.eliminationBowel}
                          </td>
                          <td className="border border-black p-1" data-id="element-3456">
                            {obs.eliminationUrine}
                          </td>
                          <td className="border border-black p-1" data-id="element-3457">
                            {obs.sign}
                          </td>
                        </tr>) : <tr data-id="element-3458">
                        <td colSpan={18} className="border border-black p-4 text-gray-400 italic" data-id="element-3459">
                          No observations recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DRUG CHART */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3460">
                <h3 className="font-bold uppercase underline mb-2 text-center" data-id="element-3461">
                  DRUG CHART
                </h3>
                <div className="flex justify-between mb-2 text-xs" data-id="element-3462">
                  <div data-id="element-3463">
                    <span className="font-semibold" data-id="element-3464">NAME:</span>{' '}
                    {selectedFile.patient.firstName}{' '}
                    {selectedFile.patient.surname}
                  </div>
                  <div data-id="element-3465">
                    <span className="font-semibold" data-id="element-3466">WARD:</span>{' '}
                    {selectedFile.drugChart?.ward}
                  </div>
                  <div data-id="element-3467">
                    <span className="font-semibold" data-id="element-3468">No.:</span>{' '}
                    {selectedFile.drugChart?.patientNo}
                  </div>
                  <div data-id="element-3469">
                    <span className="font-semibold" data-id="element-3470">SEX/AGE:</span> Female /{' '}
                    {selectedFile.patient.age}
                  </div>
                </div>

                <table className="w-full text-[9px] border-collapse border border-black text-center" data-id="element-3471">
                  <thead data-id="element-3472">
                    <tr className="bg-gray-100" data-id="element-3473">
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3474">
                        DATE
                      </th>
                      <th className="border border-black p-1 w-24" rowSpan={3} data-id="element-3475">
                        DRUG
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3476">
                        DOSAGE
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3477">
                        ROUTE OF ADMIN
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3478">
                        ORDERED BY
                      </th>
                      <th className="border border-black p-1" colSpan={21} data-id="element-3479">
                        SUPPLIED BY (DATE)
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3480">
                        SUPPLIED BY
                      </th>
                      <th className="border border-black p-1 w-12" rowSpan={3} data-id="element-3481">
                        QUANTITY
                      </th>
                      <th className="border border-black p-1 w-16" rowSpan={3} data-id="element-3482">
                        DATE
                      </th>
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3483">
                      {[0, 1, 2].map(colIdx => <th key={colIdx} className="border border-black p-1" colSpan={7} data-id="element-3484">
                          {selectedFile.drugChart?.supplyDates?.[colIdx] || ''}
                        </th>)}
                    </tr>
                    <tr className="bg-gray-100" data-id="element-3485">
                      {[0, 1, 2].map(colIdx => <Fragment key={`times-${colIdx}`} data-id="element-3486">
                          {['6', '10', '12', '14', '18', '22', '24'].map(time => <th key={`${colIdx}-${time}`} className="border border-black p-0.5 font-normal" data-id="element-3487">
                                {time}
                              </th>)}
                        </Fragment>)}
                    </tr>
                  </thead>
                  <tbody data-id="element-3488">
                    {selectedFile.drugChart?.rows && selectedFile.drugChart.rows.length > 0 ? selectedFile.drugChart.rows.map(row => <tr key={row.id} data-id="element-3489">
                          <td className="border border-black p-1" data-id="element-3490">
                            {row.date}
                          </td>
                          <td className="border border-black p-1" data-id="element-3491">
                            {row.drug}
                          </td>
                          <td className="border border-black p-1" data-id="element-3492">
                            {row.dosage}
                          </td>
                          <td className="border border-black p-1" data-id="element-3493">
                            {row.routeOfAdmin}
                          </td>
                          <td className="border border-black p-1" data-id="element-3494">
                            {row.orderedBy}
                          </td>
                          {[0, 1, 2].map(colIdx => <Fragment key={`admin-${colIdx}`} data-id="element-3495">
                              {['6', '10', '12', '14', '18', '22', '24'].map(time => {
                      const key = `${colIdx}-${time}`;
                      return <td key={key} className="border border-black p-0.5" data-id="element-3496">
                                      {row.administration?.[key] || ''}
                                    </td>;
                    })}
                            </Fragment>)}
                          <td className="border border-black p-1" data-id="element-3497">
                            {row.suppliedBy}
                          </td>
                          <td className="border border-black p-1" data-id="element-3498">
                            {row.quantity}
                          </td>
                          <td className="border border-black p-1" data-id="element-3499">
                            {row.suppliedDate}
                          </td>
                        </tr>) : <tr data-id="element-3500">
                        <td colSpan={30} className="border border-black p-4 text-gray-400 italic" data-id="element-3501">
                          No drug chart recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
                <div className="flex justify-between mt-4 text-xs" data-id="element-3502">
                  <div data-id="element-3503">
                    <span className="font-semibold" data-id="element-3504">DATE:</span>{' '}
                    {selectedFile.drugChart?.signedDate}
                  </div>
                  <div data-id="element-3505">
                    <span className="font-semibold" data-id="element-3506">SIGNATURE:</span>{' '}
                    {selectedFile.drugChart?.signedBy}
                  </div>
                </div>
              </div>

              {/* PUERPERIUM NOTES */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3507">
                <h3 className="font-bold uppercase underline mb-2" data-id="element-3508">
                  PUERPERIUM NOTES
                </h3>
                <table className="w-full text-sm border-collapse border border-black" data-id="element-3509">
                  <thead data-id="element-3510">
                    <tr className="bg-gray-100" data-id="element-3511">
                      <th className="border border-black p-2 text-left w-32" data-id="element-3512">
                        DATE
                      </th>
                      <th className="border border-black p-2 text-left" data-id="element-3513">
                        NOTES
                      </th>
                      <th className="border border-black p-2 text-left w-48" data-id="element-3514">
                        SIGNATURE
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-3515">
                    {selectedFile.puerperiumNotes.length > 0 ? selectedFile.puerperiumNotes.map(note => <tr key={note.id} data-id="element-3516">
                          <td className="border border-black p-2" data-id="element-3517">
                            {note.date}
                          </td>
                          <td className="border border-black p-2 whitespace-pre-wrap" data-id="element-3518">
                            {note.notes}
                          </td>
                          <td className="border border-black p-2" data-id="element-3519">
                            {note.signature}
                          </td>
                        </tr>) : <tr data-id="element-3520">
                        <td colSpan={3} className="border border-black p-4 text-gray-400 italic text-center" data-id="element-3521">
                          No notes recorded
                        </td>
                      </tr>}
                  </tbody>
                </table>
              </div>

              {/* DISCHARGE SLIP */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3522">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3523">
                  DISCHARGE SLIP FOR LABOUR WARD
                </h3>

                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm" data-id="element-3524">
                  <div data-id="element-3525">
                    <span className="font-semibold" data-id="element-3526">SM No:</span>{' '}
                    {selectedFile.dischargeSlip?.smNo}
                  </div>
                  <div data-id="element-3527">
                    <span className="font-semibold" data-id="element-3528">Gravida:</span>{' '}
                    {selectedFile.dischargeSlip?.gravida}
                  </div>
                  <div data-id="element-3529">
                    <span className="font-semibold" data-id="element-3530">Patient's Name:</span>{' '}
                    {selectedFile.dischargeSlip?.patientName || `${selectedFile.patient.firstName} ${selectedFile.patient.surname}`}
                  </div>
                  <div data-id="element-3531">
                    <span className="font-semibold" data-id="element-3532">Party:</span>{' '}
                    {selectedFile.dischargeSlip?.party}
                  </div>
                  <div data-id="element-3533">
                    <span className="font-semibold" data-id="element-3534">Time of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.timeOfDischarge}
                  </div>
                  <div data-id="element-3535">
                    <span className="font-semibold" data-id="element-3536">Age:</span>{' '}
                    {selectedFile.dischargeSlip?.age || selectedFile.patient.age}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3537">
                  Labour and Delivery Summary
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 mb-4 text-sm border border-black p-3" data-id="element-3538">
                  <div className="font-bold uppercase" data-id="element-3539">Mother</div>
                  <div className="font-bold uppercase" data-id="element-3540">Infant</div>
                  <div data-id="element-3541">
                    <span className="font-semibold" data-id="element-3542">D.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.motherDob}
                  </div>
                  <div data-id="element-3543">
                    <span className="font-semibold" data-id="element-3544">T.O.B:</span>{' '}
                    {selectedFile.dischargeSlip?.infantTob}
                  </div>
                  <div data-id="element-3545">
                    <span className="font-semibold" data-id="element-3546">M.O.D:</span>{' '}
                    {selectedFile.dischargeSlip?.modeOfDelivery}
                  </div>
                  <div data-id="element-3547">
                    <span className="font-semibold" data-id="element-3548">A/S:</span>{' '}
                    {selectedFile.dischargeSlip?.apgarScore}
                  </div>
                  <div data-id="element-3549">
                    <span className="font-semibold" data-id="element-3550">Perineum:</span>{' '}
                    {selectedFile.dischargeSlip?.perineum}
                  </div>
                  <div data-id="element-3551">
                    <span className="font-semibold" data-id="element-3552">Sex:</span>{' '}
                    {selectedFile.dischargeSlip?.infantSex}
                  </div>
                  <div className="col-span-2" data-id="element-3553">
                    <span className="font-semibold" data-id="element-3554">Comments:</span>{' '}
                    {selectedFile.dischargeSlip?.comments}
                  </div>
                  <div className="col-span-2" data-id="element-3555">
                    <span className="font-semibold" data-id="element-3556">P.C.A:</span>{' '}
                    {selectedFile.dischargeSlip?.pca}
                  </div>
                </div>

                <div className="flex justify-between items-center mb-2" data-id="element-3557">
                  <h4 className="font-bold uppercase underline text-sm" data-id="element-3558">
                    Discharge Summary
                  </h4>
                  <div className="text-sm" data-id="element-3559">
                    <span className="font-semibold" data-id="element-3560">Date of Discharge:</span>{' '}
                    {selectedFile.dischargeSlip?.dateOfDischarge}
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3561">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3562">Mother</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3563">
                    <div data-id="element-3564">
                      <span className="font-semibold" data-id="element-3565">BP:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bp}
                    </div>
                    <div data-id="element-3566">
                      <span className="font-semibold" data-id="element-3567">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.pallor}
                    </div>
                    <div data-id="element-3568">
                      <span className="font-semibold" data-id="element-3569">Breast:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breast}
                    </div>
                    <div data-id="element-3570">
                      <span className="font-semibold" data-id="element-3571">
                        Breastfeeding (Yes/No):
                      </span>{' '}
                      {selectedFile.dischargeSlip?.mother?.breastfeeding}
                    </div>
                    <div data-id="element-3572">
                      <span className="font-semibold" data-id="element-3573">Uterus:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.uterus}
                    </div>
                    <div data-id="element-3574">
                      <span className="font-semibold" data-id="element-3575">Perineum:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.perineum}
                    </div>
                    <div data-id="element-3576">
                      <span className="font-semibold" data-id="element-3577">Lochia:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.lochia}
                    </div>
                    <div data-id="element-3578">
                      <span className="font-semibold" data-id="element-3579">Bowels:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.bowels}
                    </div>
                    <div data-id="element-3580">
                      <span className="font-semibold" data-id="element-3581">Urine:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.urine}
                    </div>
                    <div data-id="element-3582">
                      <span className="font-semibold" data-id="element-3583">Family Planning:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.familyPlanning}
                    </div>
                    <div className="col-span-2" data-id="element-3584">
                      <span className="font-semibold" data-id="element-3585">Danger Signs:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.dangerSigns}
                    </div>
                    <div className="col-span-2" data-id="element-3586">
                      <span className="font-semibold" data-id="element-3587">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.treatment}
                    </div>
                    <div data-id="element-3588">
                      <span className="font-semibold" data-id="element-3589">Vitamin A:</span>{' '}
                      {selectedFile.dischargeSlip?.mother?.vitaminA}
                    </div>
                  </div>
                </div>

                <div className="border border-black p-3 mb-4 text-sm" data-id="element-3590">
                  <h5 className="font-bold uppercase mb-2" data-id="element-3591">Infant</h5>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-2" data-id="element-3592">
                    <div data-id="element-3593">
                      <span className="font-semibold" data-id="element-3594">BWT:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthWeight}
                    </div>
                    <div data-id="element-3595">
                      <span className="font-semibold" data-id="element-3596">Discharge Weight:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.dischargeWeight}
                    </div>
                    <div data-id="element-3597">
                      <span className="font-semibold" data-id="element-3598">Pallor:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.pallor}
                    </div>
                    <div data-id="element-3599">
                      <span className="font-semibold" data-id="element-3600">Jaundice:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.jaundice}
                    </div>
                    <div className="col-span-2" data-id="element-3601">
                      <span className="font-semibold" data-id="element-3602">
                        Passed Meconium / Urine:
                      </span>{' '}
                      {selectedFile.dischargeSlip?.infant?.passedMeconiumUrine}
                    </div>
                    <div data-id="element-3603">
                      <span className="font-semibold" data-id="element-3604">Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feeding}
                    </div>
                    <div data-id="element-3605">
                      <span className="font-semibold" data-id="element-3606">Feeding Option:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.feedingOption}
                    </div>
                    <div data-id="element-3607">
                      <span className="font-semibold" data-id="element-3608">Breast Feeding:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.breastFeeding}
                    </div>
                    <div data-id="element-3609">
                      <span className="font-semibold" data-id="element-3610">BCG:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.bcg}
                    </div>
                    <div data-id="element-3611">
                      <span className="font-semibold" data-id="element-3612">OPV:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.opv}
                    </div>
                    <div data-id="element-3613">
                      <span className="font-semibold" data-id="element-3614">Birth Record:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.birthRecord}
                    </div>
                    <div data-id="element-3615">
                      <span className="font-semibold" data-id="element-3616">G/C:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.gc}
                    </div>
                    <div className="col-span-2" data-id="element-3617">
                      <span className="font-semibold" data-id="element-3618">Treatment:</span>{' '}
                      {selectedFile.dischargeSlip?.infant?.treatment}
                    </div>
                  </div>
                </div>

                <div className="space-y-2 text-sm" data-id="element-3619">
                  <div data-id="element-3620">
                    <span className="font-semibold" data-id="element-3621">
                      6 days Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixDaysPostnatalDate}
                  </div>
                  <div data-id="element-3622">
                    <span className="font-semibold" data-id="element-3623">
                      6 Weeks Postnatal Date:
                    </span>{' '}
                    {selectedFile.dischargeSlip?.sixWeeksPostnatalDate}
                  </div>
                  <div data-id="element-3624">
                    <span className="font-semibold" data-id="element-3625">Name in Full:</span>{' '}
                    {selectedFile.dischargeSlip?.nameInFull}
                  </div>
                </div>
              </div>

              {/* POSTNATAL AT ONE WEEK */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3626">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3627">
                  Postnatal at One Week
                </h3>
                <div className="text-sm mb-3" data-id="element-3628">
                  <span className="font-semibold" data-id="element-3629">Date:</span>{' '}
                  {selectedFile.postNatalOneWeek?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3630">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-3631">
                  <div data-id="element-3632">
                    <span className="font-semibold" data-id="element-3633">General Well Being:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-3634">
                    <span className="font-semibold" data-id="element-3635">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.temp}
                  </div>
                  <div data-id="element-3636">
                    <span className="font-semibold" data-id="element-3637">Pulse:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.pulse}
                  </div>
                  <div data-id="element-3638">
                    <span className="font-semibold" data-id="element-3639">BP:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.bp}
                  </div>
                  <div data-id="element-3640">
                    <span className="font-semibold" data-id="element-3641">Urine:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.urine}
                  </div>
                  <div data-id="element-3642">
                    <span className="font-semibold" data-id="element-3643">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.head}
                  </div>
                  <div data-id="element-3644">
                    <span className="font-semibold" data-id="element-3645">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.eyes}
                  </div>
                  <div data-id="element-3646">
                    <span className="font-semibold" data-id="element-3647">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ears}
                  </div>
                  <div data-id="element-3648">
                    <span className="font-semibold" data-id="element-3649">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.mouth}
                  </div>
                  <div data-id="element-3650">
                    <span className="font-semibold" data-id="element-3651">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.neck}
                  </div>
                  <div data-id="element-3652">
                    <span className="font-semibold" data-id="element-3653">Breasts:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.breasts}
                  </div>
                  <div data-id="element-3654">
                    <span className="font-semibold" data-id="element-3655">Ampills:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.ampills}
                  </div>
                  <div data-id="element-3656">
                    <span className="font-semibold" data-id="element-3657">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-3658">
                    <span className="font-semibold" data-id="element-3659">Groin:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.groin}
                  </div>
                  <div data-id="element-3660">
                    <span className="font-semibold" data-id="element-3661">Perineum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.perineum}
                  </div>
                  <div data-id="element-3662">
                    <span className="font-semibold" data-id="element-3663">Legs:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.legs}
                  </div>
                  <div data-id="element-3664">
                    <span className="font-semibold" data-id="element-3665">Anus:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.anus}
                  </div>
                  <div data-id="element-3666">
                    <span className="font-semibold" data-id="element-3667">Lochia:</span>{' '}
                    {selectedFile.postNatalOneWeek?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3668">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-3669">
                  <div data-id="element-3670">
                    <span className="font-semibold" data-id="element-3671">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3672">
                      {selectedFile.postNatalOneWeek?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-3673">
                    <span className="font-semibold" data-id="element-3674">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3675">
                      {selectedFile.postNatalOneWeek?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-3676">
                    <span className="font-semibold" data-id="element-3677">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3678">
                      {selectedFile.postNatalOneWeek?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3679">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3680">
                  <div data-id="element-3681">
                    <span className="font-semibold" data-id="element-3682">Infant:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.name}
                  </div>
                  <div data-id="element-3683">
                    <span className="font-semibold" data-id="element-3684">Weight:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.weight}
                  </div>
                  <div data-id="element-3685">
                    <span className="font-semibold" data-id="element-3686">Date:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3687">
                  <div data-id="element-3688">
                    <span className="font-semibold" data-id="element-3689">Head Circumference:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.headCircumference}
                  </div>
                  <div data-id="element-3690">
                    <span className="font-semibold" data-id="element-3691">Length:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.length}
                  </div>
                  <div data-id="element-3692">
                    <span className="font-semibold" data-id="element-3693">Temp:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.temp}
                  </div>
                  <div data-id="element-3694">
                    <span className="font-semibold" data-id="element-3695">Head:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.head}
                  </div>
                  <div data-id="element-3696">
                    <span className="font-semibold" data-id="element-3697">Eyes:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.eyes}
                  </div>
                  <div data-id="element-3698">
                    <span className="font-semibold" data-id="element-3699">Ears:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.ears}
                  </div>
                  <div data-id="element-3700">
                    <span className="font-semibold" data-id="element-3701">Mouth:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.mouth}
                  </div>
                  <div data-id="element-3702">
                    <span className="font-semibold" data-id="element-3703">Neck:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neck}
                  </div>
                  <div data-id="element-3704">
                    <span className="font-semibold" data-id="element-3705">Chest:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.chest}
                  </div>
                  <div data-id="element-3706">
                    <span className="font-semibold" data-id="element-3707">Abdomen:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.abdomen}
                  </div>
                  <div data-id="element-3708">
                    <span className="font-semibold" data-id="element-3709">Cord:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.cord}
                  </div>
                  <div data-id="element-3710">
                    <span className="font-semibold" data-id="element-3711">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-3712">
                    <span className="font-semibold" data-id="element-3713">Extremities:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.extremities}
                  </div>
                  <div data-id="element-3714">
                    <span className="font-semibold" data-id="element-3715">Back:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.back}
                  </div>
                  <div data-id="element-3716">
                    <span className="font-semibold" data-id="element-3717">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.neurologicExam}
                  </div>
                  <div data-id="element-3718">
                    <span className="font-semibold" data-id="element-3719">Feeding:</span>{' '}
                    {selectedFile.postNatalOneWeek?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-3720">
                  <span className="font-semibold" data-id="element-3721">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-3722">
                    {selectedFile.postNatalOneWeek?.infant.additionalNotes}
                  </p>
                </div>
              </div>

              {/* POSTNATAL AT SIX WEEKS */}
              <div className="mb-6 pdf-page-break print:break-before-page" data-id="element-3723">
                <h3 className="font-bold uppercase underline mb-3 text-center" data-id="element-3724">
                  Postnatal at Six Weeks
                </h3>
                <div className="text-sm mb-3" data-id="element-3725">
                  <span className="font-semibold" data-id="element-3726">Date:</span>{' '}
                  {selectedFile.postNatalSixWeeks?.date}
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3727">
                  Mother
                </h4>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-4" data-id="element-3728">
                  <div data-id="element-3729">
                    <span className="font-semibold" data-id="element-3730">General Well Being:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.generalWellBeing}
                  </div>
                  <div data-id="element-3731">
                    <span className="font-semibold" data-id="element-3732">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.temp}
                  </div>
                  <div data-id="element-3733">
                    <span className="font-semibold" data-id="element-3734">Pulse:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.pulse}
                  </div>
                  <div data-id="element-3735">
                    <span className="font-semibold" data-id="element-3736">BP:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.bp}
                  </div>
                  <div data-id="element-3737">
                    <span className="font-semibold" data-id="element-3738">Urine:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.urine}
                  </div>
                  <div data-id="element-3739">
                    <span className="font-semibold" data-id="element-3740">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.head}
                  </div>
                  <div data-id="element-3741">
                    <span className="font-semibold" data-id="element-3742">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.eyes}
                  </div>
                  <div data-id="element-3743">
                    <span className="font-semibold" data-id="element-3744">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ears}
                  </div>
                  <div data-id="element-3745">
                    <span className="font-semibold" data-id="element-3746">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.mouth}
                  </div>
                  <div data-id="element-3747">
                    <span className="font-semibold" data-id="element-3748">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.neck}
                  </div>
                  <div data-id="element-3749">
                    <span className="font-semibold" data-id="element-3750">Breasts:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.breasts}
                  </div>
                  <div data-id="element-3751">
                    <span className="font-semibold" data-id="element-3752">Ampills:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.ampills}
                  </div>
                  <div data-id="element-3753">
                    <span className="font-semibold" data-id="element-3754">Abdomen (Uterus):</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.abdomenUterus}
                  </div>
                  <div data-id="element-3755">
                    <span className="font-semibold" data-id="element-3756">Groin:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.groin}
                  </div>
                  <div data-id="element-3757">
                    <span className="font-semibold" data-id="element-3758">Perineum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.perineum}
                  </div>
                  <div data-id="element-3759">
                    <span className="font-semibold" data-id="element-3760">Legs:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.legs}
                  </div>
                  <div data-id="element-3761">
                    <span className="font-semibold" data-id="element-3762">Anus:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.anus}
                  </div>
                  <div data-id="element-3763">
                    <span className="font-semibold" data-id="element-3764">Lochia:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.mother.lochia}
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3765">
                  Vaginal Examination
                </h4>
                <div className="space-y-2 text-sm mb-4" data-id="element-3766">
                  <div data-id="element-3767">
                    <span className="font-semibold" data-id="element-3768">Cervix per speculum:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3769">
                      {selectedFile.postNatalSixWeeks?.mother.cervixPerSpeculum}
                    </span>
                  </div>
                  <div data-id="element-3770">
                    <span className="font-semibold" data-id="element-3771">Family Planning:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3772">
                      {selectedFile.postNatalSixWeeks?.mother.familyPlanning}
                    </span>
                  </div>
                  <div data-id="element-3773">
                    <span className="font-semibold" data-id="element-3774">Treatment or advise:</span>{' '}
                    <span className="whitespace-pre-wrap" data-id="element-3775">
                      {selectedFile.postNatalSixWeeks?.mother.treatmentOrAdvise}
                    </span>
                  </div>
                </div>

                <h4 className="font-bold uppercase underline mb-2 text-sm" data-id="element-3776">
                  Infant Examination Form
                </h4>
                <div className="grid grid-cols-3 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3777">
                  <div data-id="element-3778">
                    <span className="font-semibold" data-id="element-3779">Infant:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.name}
                  </div>
                  <div data-id="element-3780">
                    <span className="font-semibold" data-id="element-3781">Weight:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.weight}
                  </div>
                  <div data-id="element-3782">
                    <span className="font-semibold" data-id="element-3783">Date:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.date}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-1 text-sm mb-3" data-id="element-3784">
                  <div data-id="element-3785">
                    <span className="font-semibold" data-id="element-3786">Head Circumference:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.headCircumference}
                  </div>
                  <div data-id="element-3787">
                    <span className="font-semibold" data-id="element-3788">Length:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.length}
                  </div>
                  <div data-id="element-3789">
                    <span className="font-semibold" data-id="element-3790">Temp:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.temp}
                  </div>
                  <div data-id="element-3791">
                    <span className="font-semibold" data-id="element-3792">Head:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.head}
                  </div>
                  <div data-id="element-3793">
                    <span className="font-semibold" data-id="element-3794">Eyes:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.eyes}
                  </div>
                  <div data-id="element-3795">
                    <span className="font-semibold" data-id="element-3796">Ears:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.ears}
                  </div>
                  <div data-id="element-3797">
                    <span className="font-semibold" data-id="element-3798">Mouth:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.mouth}
                  </div>
                  <div data-id="element-3799">
                    <span className="font-semibold" data-id="element-3800">Neck:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neck}
                  </div>
                  <div data-id="element-3801">
                    <span className="font-semibold" data-id="element-3802">Chest:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.chest}
                  </div>
                  <div data-id="element-3803">
                    <span className="font-semibold" data-id="element-3804">Abdomen:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.abdomen}
                  </div>
                  <div data-id="element-3805">
                    <span className="font-semibold" data-id="element-3806">Cord:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.cord}
                  </div>
                  <div data-id="element-3807">
                    <span className="font-semibold" data-id="element-3808">Genitalia & Rectum:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.genitaliaRectum}
                  </div>
                  <div data-id="element-3809">
                    <span className="font-semibold" data-id="element-3810">Extremities:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.extremities}
                  </div>
                  <div data-id="element-3811">
                    <span className="font-semibold" data-id="element-3812">Back:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.back}
                  </div>
                  <div data-id="element-3813">
                    <span className="font-semibold" data-id="element-3814">Neurologic Exam:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.neurologicExam}
                  </div>
                  <div data-id="element-3815">
                    <span className="font-semibold" data-id="element-3816">Feeding:</span>{' '}
                    {selectedFile.postNatalSixWeeks?.infant.feeding}
                  </div>
                </div>
                <div className="text-sm" data-id="element-3817">
                  <span className="font-semibold" data-id="element-3818">Additional Notes:</span>
                  <p className="whitespace-pre-wrap mt-1" data-id="element-3819">
                    {selectedFile.postNatalSixWeeks?.infant.additionalNotes}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>}
    </div>;
};